import { getEffectiveRole } from '@/lib/rbac';
 
const ORGANIZER_API_ROLES = new Set([
  'super_admin',
  'admin',
  'organization_admin',
  'organization_manager',
  'organizer',
  'individual_organizer',
  // Sidebar aliases that manage events under the organizer shell
  'event_manager',
  'team_manager',
]);

export type OrganizerAccessUser = {
  role?: string;
  user_type?: string;
  roles?: string[] | null;
} | null | undefined;

/** Organization admins/managers have organizer access for their workspace. */
export function hasOrganizerCapability(user: OrganizerAccessUser): boolean {
  return canAccessOrganizerApis(user);
}

/** Roles that may call /organizer/* APIs (org admins, managers, and organizers). */
export function canAccessOrganizerApis(user: OrganizerAccessUser): boolean {
  if (!user) return false;

  const effective = getEffectiveRole(user);
  if (ORGANIZER_API_ROLES.has(effective)) {
    return true;
  }

  const roles = Array.isArray(user.roles)
    ? user.roles.map((r) => String(r).trim().toLowerCase())
    : [];

  return roles.some((r) => ORGANIZER_API_ROLES.has(r));
}

/**
 * Roles that may use organizer dashboard features (individual organizers and
 * organization staff managing events).
 */
export function canUseOrganizerDashboardApis(user: OrganizerAccessUser): boolean {
  return canAccessOrganizerApis(user);
}

/** Read persisted auth state for axios interceptors (before React hydration). */
export function canAccessOrganizerApisFromStorage(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const raw = localStorage.getItem('auth-storage');
    if (raw) {
      const parsed = JSON.parse(raw);
      const user = parsed?.state?.user;
      if (canAccessOrganizerApis(user)) {
        return true;
      }
    }
  } catch {
    // Ignore malformed persisted auth state.
  }

  return false;
}
